import type { TrackWarning, TrackWarningKind } from '@/lib/db/types';

export interface WarningLine {
  kind: TrackWarningKind;
  message: string;
}

/**
 * One line per kind of warning, however many of that kind the track carries.
 *
 * `normaliseTrack` now summarises repeated coverage gaps into a single warning,
 * but a track is an immutable import: every file read before that change still
 * has one warning per gap stored against it, and a patchy ADS-B track can carry
 * dozens. Stacked, they push everything else off the card. So the list collapses
 * repeats at render too rather than waiting for a re-import that may not be
 * possible — a track fetched from a live API has no file to read again.
 *
 * The first message of a kind is kept verbatim because it is the specific one
 * the pilot can act on; the rest become a count, which is what the pilot
 * actually needs from them.
 */
export function collapseWarnings(warnings: readonly TrackWarning[]): WarningLine[] {
  const byKind = new Map<TrackWarningKind, { message: string; count: number }>();

  for (const warning of warnings) {
    const seen = byKind.get(warning.kind);
    if (seen === undefined) {
      byKind.set(warning.kind, { message: warning.message, count: 1 });
    } else {
      seen.count += 1;
    }
  }

  return [...byKind].map(([kind, { message, count }]) => ({
    kind,
    message: count === 1 ? message : `${message} ${String(count - 1)} more like it in this track.`,
  }));
}
